import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer
} from 'components/Filters'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { Input } from 'components/Input'
import { EBusinessCustomerStatus, IBusinessCustomersFiltersForm } from 'features/BusinessCustomer/types'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import {
  GTBusinessClientAutocomplete,
  GTBusinessCustomerSuggestion,
  GTBusinessSiteSuggestion
} from 'gql/types.generated'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import SelectKreditsCategory from 'features/Kredits/components/SelectKreditsCategory'
import { BusinessCustomerAutocomplete } from 'features/BusinessCustomer/components/BusinessCustomerAutocomplete'
import { businessCustomerStatusMessages } from 'features/BusinessCustomer/components/Status/messages'
import { intToLetter } from 'utils/number'

const messages = defineMessages({
  selectStatus: {
    id: 'trips.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  phonePlaceholder: {
    id: 'businessCustomer.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  emailPlaceholder: {
    id: 'businessCustomer.email.placeholder',
    defaultMessage: 'Enter an email'
  },
  userIdPlaceholder: {
    id: 'businessCustomer.userId.placeholder',
    defaultMessage: 'Enter a user ID'
  },
  customerIdPlaceholder: {
    id: 'businessCustomer.customerId.placeholder',
    defaultMessage: 'Enter a customer ID'
  },
  customerNamePlaceholder: {
    id: 'businessCustomer.customerName.placeholder',
    defaultMessage: 'Enter a customer name'
  },
  businessClientPlaceholder: {
    id: 'businessCustomer.client.placeholder',
    defaultMessage: 'Enter a business client'
  },
  businessSitePlaceholder: {
    id: 'businessCustomer.site.placeholder',
    defaultMessage: 'Enter a business site'
  }
})

const defaultValues: IBusinessCustomersFiltersForm = {
  status: '',
  phone: '',
  email: '',
  userId: '',
  customerId: '',
  category: '',
  client: {
    id: '',
    name: ''
  },
  site: {
    id: '',
    name: ''
  },
  businessCustomers: []
}

interface IBusinessCustomersFiltersProps {
  filters: IBusinessCustomersFiltersForm
  onSubmit: (values: IBusinessCustomersFiltersForm) => void
}

export const BusinessCustomersFilters = ({ filters, onSubmit }: IBusinessCustomersFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IBusinessCustomersFiltersForm = {
    status: filters.status || defaultValues.status,
    phone: filters.phone || defaultValues.phone,
    email: filters.email || defaultValues.email,
    userId: filters.userId || defaultValues.userId,
    customerId: filters.customerId || defaultValues.customerId,
    client: filters.client || defaultValues.client,
    site: filters.site || defaultValues.site,
    category: filters.category ?? defaultValues.category,
    businessCustomers: filters.businessCustomers || defaultValues.businessCustomers
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          onSubmit({
            ...values,
            [id]: value
          })
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value =
                            businessCustomerStatusMessages[filterValue as EBusinessCustomerStatus] &&
                            intl.formatMessage(businessCustomerStatusMessages[filterValue as EBusinessCustomerStatus])
                          break
                        case 'phone':
                          label = <FormattedMessage id="businessCustomer.phone.label" defaultMessage="Phone" />
                          value = filterValue
                          break
                        case 'email':
                          label = <FormattedMessage id="businessCustomer.email.label" defaultMessage="EMAIL" />
                          value = filterValue
                          break
                        case 'userId':
                          label = <FormattedMessage id="businessCustomer.userId.label" defaultMessage="USER ID" />
                          value = filterValue
                          break
                        case 'customerId':
                          label = (
                            <FormattedMessage
                              id="businessCustomer.customerId.label"
                              defaultMessage="CUSTOMER USER ID"
                            />
                          )
                          value = filterValue
                          break
                        case 'client':
                          label = (
                            <FormattedMessage id="businessCustomer.client.label" defaultMessage="Business Client" />
                          )
                          value = (filterValue as { id: string; name: string }).name
                          break

                        case 'site':
                          label = <FormattedMessage id="businessCustomer.site.label" defaultMessage="Business Site" />
                          value = (filterValue as { id: string; name: string }).name
                          break
                        case 'category':
                          label = (
                            <FormattedMessage id="businessCustomer.filters.label.category" defaultMessage="Category" />
                          )
                          value = intToLetter(filterValue as unknown as number)
                          break
                        case 'businessCustomers':
                          label = (
                            <FormattedMessage id="businessCustomer.customerName.label" defaultMessage="Customer Name" />
                          )
                          value = (filterValue as GTBusinessCustomerSuggestion[]).at(0)?.name
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof filters)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <BusinessCustomerAutocomplete
                    id="businessCustomers"
                    value={values.businessCustomers.at(0)?.name}
                    label={<FormattedMessage id="businessCustomer.customerName.label" defaultMessage="Customer Name" />}
                    placeholder={intl.formatMessage(messages.customerNamePlaceholder)}
                    onChange={(businessCustomers: GTBusinessCustomerSuggestion[]): void => {
                      setFieldValue('businessCustomers', businessCustomers)
                      onSubmit({
                        ...values,
                        businessCustomers
                      })
                    }}
                    uniqueName
                  />
                  <Input
                    id="customerId"
                    onChange={handleChange}
                    value={values.customerId}
                    placeholder={intl.formatMessage(messages.customerIdPlaceholder)}
                    label={<FormattedMessage id="businessCustomer.email.label" defaultMessage="CUSTOMER ID" />}
                  />
                  <Input
                    id="userId"
                    onChange={handleChange}
                    value={values.userId}
                    placeholder={intl.formatMessage(messages.userIdPlaceholder)}
                    label={<FormattedMessage id="businessCustomer.email.label" defaultMessage="USER ID" />}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    <option value={EBusinessCustomerStatus.ACTIVE}>
                      <FormattedMessage id="dictionary.active" defaultMessage="Active" />
                    </option>
                    <option value={EBusinessCustomerStatus.INACTIVE}>
                      <FormattedMessage id="dictionary.inactive" defaultMessage="Inactive" />
                    </option>
                  </Select>
                  <Input
                    id={'phone'}
                    type="tel"
                    value={values.phone}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.phonePlaceholder)}
                    label={<FormattedMessage id="businessCustomer.phone.label" defaultMessage="Phone" />}
                  />
                  <Input
                    id="email"
                    onChange={handleChange}
                    value={values.email}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    label={<FormattedMessage id="businessCustomer.email.label" defaultMessage="EMAIL" />}
                  />
                  <SelectKreditsCategory
                    id={'category'}
                    label={
                      <FormattedMessage id="businessCustomer.customerName.label.category" defaultMessage="Category" />
                    }
                    name="category"
                    onChange={handleSelectChange}
                  />
                  <BusinessClientAutocomplete
                    id="clientId"
                    value={values.client.name}
                    placeholder={intl.formatMessage(messages.businessClientPlaceholder)}
                    onChange={(businessClient: GTBusinessClientAutocomplete): void => {
                      setFieldValue('client', businessClient)
                      onSubmit({
                        ...values,
                        client: businessClient
                      })
                    }}
                    label={<FormattedMessage id="businessCustomer.client.label" defaultMessage="Business Client" />}
                  />
                  <BusinessSiteAutocomplete
                    id="siteId"
                    value={values.site.name}
                    placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
                    onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                      setFieldValue('site', businessSite)
                      onSubmit({
                        ...values,
                        site: businessSite
                      })
                    }}
                    label={<FormattedMessage id="businessCustomer.site.label" defaultMessage="Business Site" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <button type="submit" hidden>
              Submit
            </button>
          </form>
        )
      }}
    </Formik>
  )
}
